import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import WobaLogo from "@/assets/woba.svg";
import { formatUTCDate } from "../utils";
import { useProposalT } from "../i18n/ProposalI18nContext";
import { LanguageSelector } from "./LanguageSelector";
import { formatProposalDate } from "@/features/vision-360/utils/format-proposal-date";

interface CTASectionProps {
  proposalValidity?: string;
  showScheduling?: boolean;
  visitSelectedIds?: Set<string>;
  onSchedule?: () => void;
  createdAt?: string;
}

const LOGOS_ROW1 = [
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/68016073ed1b2d1d99f77899_IFood_logo.svg-p-500.png",
    alt: "iFood",
    height: 32,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/6801609db853dd22d771dc6a_Logotipo_Vale.svg-p-500.png",
    alt: "Vale",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/680160c7e0a451ae41fe3f9f_Bosch-logo.svg-p-500.png",
    alt: "Bosch",
    height: 32,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/6801628acfd8b591d3c4bbb2_Banco_Bradesco_logo_(horizontal)-p-500.png",
    alt: "Bradesco",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/68016293ed1b2d1d99f90c32_Logo-Stone.svg",
    alt: "Stone",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/6801627424c0898c370feb59_Volkswagen_logo_2019.svg.avif",
    alt: "Volkswagen",
    height: 36,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/680160be45a91a5fcff1366c_Cielo_S.A._logo.svg.avif",
    alt: "Cielo",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/680160d4a70e5501a039bc49_xp%20logo.avif",
    alt: "XP",
    height: 28,
  },
];

const LOGOS_ROW2 = [
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/68016252a70e5501a03ae60e_2023_Itau%CC%81_Unibanco_Logo-p-500.avif",
    alt: "Itaú",
    height: 30,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/6801626c79de4d7a501a54e6_QuintoAndar_Logo_2022.svg-p-500.avif",
    alt: "QuintoAndar",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/680160a542e3dfdf90d20ee7_mercado-pago-logo-p-500.avif",
    alt: "Mercado Pago",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/68016263336969d4fbbee02f_prudential-7.svg",
    alt: "Prudential",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/680162b5e57ff51f02604c8b_GUPYY-p-500.webp",
    alt: "Gupy",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/68016095600725d9ebf748a0_inter.avif",
    alt: "Inter",
    height: 28,
  },
  {
    src: "https://cdn.prod.website-files.com/65525e64ec04b0422261a2ad/6895f75ae2ddb562b9850013_zurich-p-500.png",
    alt: "Zurich",
    height: 28,
  },
];

export function LogosSection() {
  const { t } = useProposalT();

  return (
    <>
      <style>{`
        @keyframes marqueeLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marqueeRight {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
      <section
        className="py-16 px-6 overflow-hidden"
        style={{ background: "#F5F3EF", borderTop: "1px solid #E8E6E3" }}
      >
        <div className="max-w-6xl mx-auto text-center mb-10">
          <p className="text-sm font-medium" style={{ color: "#78736D" }}>
            {t("logos.socialProof")}
          </p>
        </div>

        {/* Row 1 — scrolls left */}
        <div
          style={{
            display: "flex",
            gap: "48px",
            alignItems: "center",
            animation: "marqueeLeft 30s linear infinite",
            width: "max-content",
          }}
        >
          {[...LOGOS_ROW1, ...LOGOS_ROW1].map((logo, i) => (
            <img
              key={i}
              src={logo.src}
              alt={logo.alt}
              style={{
                height: `${logo.height}px`,
                objectFit: "contain",
                filter: "grayscale(1)",
                opacity: 0.55,
                transition: "all 0.3s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(0)";
                (e.currentTarget as HTMLImageElement).style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(1)";
                (e.currentTarget as HTMLImageElement).style.opacity = "0.55";
              }}
            />
          ))}
        </div>

        {/* Row 2 — scrolls right */}
        <div
          style={{
            display: "flex",
            gap: "48px",
            alignItems: "center",
            animation: "marqueeRight 35s linear infinite",
            width: "max-content",
            marginTop: "24px",
          }}
        >
          {[...LOGOS_ROW2, ...LOGOS_ROW2].map((logo, i) => (
            <img
              key={i}
              src={logo.src}
              alt={logo.alt}
              style={{
                height: `${logo.height}px`,
                objectFit: "contain",
                filter: "grayscale(1)",
                opacity: 0.55,
                transition: "all 0.3s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(0)";
                (e.currentTarget as HTMLImageElement).style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLImageElement).style.filter =
                  "grayscale(1)";
                (e.currentTarget as HTMLImageElement).style.opacity = "0.55";
              }}
            />
          ))}
        </div>
      </section>
    </>
  );
}

export function CTASection({
  proposalValidity,
  showScheduling = true,
  visitSelectedIds,
  onSchedule,
  createdAt,
}: CTASectionProps) {
  const { t } = useProposalT();
  const [stickyVisible, setStickyVisible] = useState(false);

  const formattedDate = proposalValidity
    ? formatUTCDate(Number(proposalValidity))
    : t("cta.validTBD");

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom =
        scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 120;
      setStickyVisible(scrollY > 300 && !nearBottom);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Footer */}
      <footer
        className="py-12 px-6"
        style={{ borderTop: "1px solid #E8E6E3", background: "#FDFBF7" }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={WobaLogo} alt="Woba" className="h-6 w-auto" />
              </div>
              <p className="text-sm text-[#78736D]">{t("cta.tagline")}</p>
            </div>

            <div>
              <h3 className="text-base font-bold text-[#1A1814] mb-4">
                {t("cta.contact")}
              </h3>
              <a
                href="https://www.woba.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#78736D] hover:text-[#1A1814] transition-colors"
              >
                <Globe className="w-4 h-4" />
                www.woba.com
              </a>
            </div>

            <div>
              <h3 className="text-base font-bold text-[#1A1814] mb-4">
                {t("cta.proposal")}
              </h3>
              {createdAt && (
                <p className="text-sm text-[#78736D] mb-1">
                  <span className="font-semibold text-[#1A1814]">
                    {t("cta.generatedOn")}
                  </span>{" "}
                  {formatProposalDate(createdAt)}
                </p>
              )}
              <p className="text-sm text-[#78736D]">
                <span className="font-semibold text-[#1A1814]">
                  {t("cta.validUntil")}
                </span>{" "}
                {formattedDate}
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E8E6E3] flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-[#9E9A94] m-0">
              {t("cta.copyright", { year: new Date().getFullYear() })}
            </p>
            <div className="flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-[#9E9A94]" />
              <LanguageSelector />
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky CTA bar */}
      {showScheduling && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 40,
            background: "#1A1814",
            borderTop: "2px solid #FFD900",
            padding: "12px 24px",
            transform: stickyVisible ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.35s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="hidden md:block">
              <p className="text-sm font-semibold" style={{ color: "white" }}>
                {t("cta.pricesGuaranteedUntil")}{" "}
                <span style={{ color: "#FFD900" }}>{formattedDate}</span>
              </p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                {t("cta.afterDate")}
              </p>
            </div>
            <button
              onClick={() => {
                const count = visitSelectedIds?.size ?? 0;
                if (count > 0 && onSchedule) {
                  onSchedule();
                } else {
                  document
                    .getElementById("comparativo")
                    ?.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="flex items-center justify-center gap-2 w-full md:w-auto md:ml-auto px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex-shrink-0"
              style={{
                background: "#FFD900",
                color: "#1A1814",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(255,217,0,0.3)",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {(() => {
                const count = visitSelectedIds?.size ?? 0;
                if (count === 0) return t("cta.chooseSpaces");
                if (count === 1)
                  return t("cta.scheduleVisitsCount", {
                    count: String(count),
                  });
                return t("cta.scheduleVisitsCountPlural", {
                  count: String(count),
                });
              })()}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
